import React from 'react';
import {Card, CardContent, Chip, IconButton, Stack, Tooltip, Typography} from '@mui/material';
import {Email, Phone} from '@mui/icons-material';
import SendMessageButton from '../../../../../../components/SendMessageButton';
import UserAvatar from '../../../../../../components/UserAvatar';

const ExpertCard = ({expert, compact = false}) => {
    // Generate initials from display name (first letter of first and last name)
    // Only for fake users without uid
    const getInitials = (name) => {
        if (!name) return null;
        const parts = name.trim().split(' ');
        if (parts.length === 1) return parts[0][0].toUpperCase();
        return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    };

    // For fake users (no uid), show initials via UserAvatar fallback.
    const avatarContent = expert.uid
        ? null
        : getInitials(expert.displayName);

    return (
        <Card variant="outlined" sx={{mb: 1.5}}>
            <CardContent sx={{py: 1.5, px: 2, '&:last-child': {pb: 1.5}}}>
                <Stack direction="row" spacing={2} alignItems="flex-start">
                    <UserAvatar
                        user={avatarContent ? {...expert, displayName: expert.displayName || avatarContent} : expert}
                        userId={expert.uid}
                        sx={{width: compact ? 40 : 56, height: compact ? 40 : 56}}
                    />
                    <Stack flex={1} spacing={0.5}>
                        <Stack direction="row" alignItems="center" justifyContent="space-between">
                            <Typography variant="subtitle2" fontWeight={600}>
                                {expert.displayName}
                            </Typography>
                            {expert.matchScore && (
                                <Chip
                                    size="small"
                                    label={`${expert.matchScore}% match`}
                                    color={expert.matchScore >= 90 ? 'success' : 'primary'}
                                    sx={{fontSize: '0.7rem', height: 20}}
                                />
                            )}
                        </Stack>
                        <Typography variant="caption" color="text.secondary">
                            {expert.title}
                        </Typography>
                        {!compact && (
                            <>
                                <Typography variant="caption" color="text.secondary">
                                    {expert.office} • {expert.organization}
                                </Typography>
                                {expert.specialties && (
                                    <Stack
                                        direction="row"
                                        gap={0.5}
                                        flexWrap="wrap"
                                        mt={0.5}
                                        sx={{maxWidth: '100%'}}
                                    >
                                        {expert.specialties.slice(0, 3).map(s => (
                                            <Chip
                                                key={s}
                                                label={s}
                                                size="small"
                                                variant="outlined"
                                                sx={{
                                                    fontSize: '0.65rem',
                                                    height: 18,
                                                    maxWidth: '100%',
                                                    '& .MuiChip-label': {
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        whiteSpace: 'nowrap',
                                                        display: 'block'
                                                    }
                                                }}
                                            />
                                        ))}
                                    </Stack>
                                )}
                                {expert.relevanceReason && (
                                    <Typography
                                        variant="caption"
                                        color="primary.main"
                                        sx={{mt: 1, display: 'block', fontStyle: 'italic'}}
                                    >
                                        {expert.relevanceReason}
                                    </Typography>
                                )}
                            </>
                        )}
                        <Stack direction="row" spacing={0.5} mt={1}>
                            <Tooltip title="Send email">
                                <IconButton size="small" href={`mailto:${expert.email}`}>
                                    <Email fontSize="small"/>
                                </IconButton>
                            </Tooltip>
                            {expert.cellPhone && (
                                <Tooltip title="Call">
                                    <IconButton size="small" href={`tel:${expert.cellPhone}`}>
                                        <Phone fontSize="small"/>
                                    </IconButton>
                                </Tooltip>
                            )}
                            <SendMessageButton
                                user={expert}
                                isIcon={true}
                                size="small"
                            />
                        </Stack>
                    </Stack>
                </Stack>
            </CardContent>
        </Card>
    );
};

export default ExpertCard;
